/**
 * AppErrorBoundary — last line of defense. No render error may ever
 * leave the user staring at a blank white page. Chunk-load errors
 * trigger ONE automatic reload (fresh hashes); anything else shows a
 * friendly recovery card in the Graphite & Sky theme.
 */
import { Component } from "react";

const RELOAD_KEY = "maldek-boundary-reload-at";

function isChunkError(err) {
  const msg = String((err && err.message) || err || "");
  return /chunk|dynamically imported module|import\(\)|Loading CSS chunk/i.test(msg);
}

export default class AppErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* noop */ }
      if (Date.now() - last > 60000) {
        try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* noop */ }
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#faf6ee", fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
      >
        <div
          className="max-w-md w-full rounded-3xl p-8 text-center"
          style={{ background: "#fffdfa", border: "1px solid #e3dccb", boxShadow: "0 24px 60px rgba(15,42,61,0.10)" }}
        >
          <img src="/brand/icon.png" alt="Maldek" className="h-12 w-12 rounded-xl mx-auto mb-4 object-cover" />
          <h1 className="text-[22px] font-extrabold tracking-tight" style={{ color: "#2b261c", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
            Quick hiccup — one tap fixes it.
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "#5d5748" }}>
            A fresh version of Maldek just shipped. Reload to pick it up — your place is saved.
          </p>
          <button
            data-testid="app-error-reload-btn"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 text-[14px] font-bold text-white transition-transform hover:-translate-y-0.5"
            style={{ background: "linear-gradient(135deg, #6B8F71, #6B8F71)", boxShadow: "0 12px 28px rgba(107,143,113,0.27)" }}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
